import { buildPuzzleLevel, PUZZLE_LEVEL_COUNT, puzzleTotalScore } from '~/data/puzzles'
import { puzzleClearStardust } from '~/composables/useStardust'
import { FOREST_THEMES, forestById, isLandscapeViewport, poiTravelLength, puzzleHallId } from '~/data/forests'
import { TUNING } from '~/config/tuning'
import type { Creature, RunState } from '~/types/game'
import { makeId } from '~/utils/random'

const PROGRESS_KEY = 'elemental-dispatch-puzzle-v1'
/** The player's REAL run parks here while a puzzle session runs. */
const STASH_KEY = 'elemental-dispatch-run-stash-v1'

/**
 * 🧩 Puzzle Game sessions (v1.28, per Javi). A session is a disposable
 * RunState (phase 'encounter', one puzzle node) that borrows the whole
 * encounter engine: the real run's save is stashed first and restored
 * when the session ends, however it ends — finishing, giving up, or
 * landing back on the title screen after a crash.
 */
export function usePuzzleGame() {
  const run = useRunState()
  /** Best (lowest) damage per completed level. */
  const best = useState<Record<number, number>>('puzzle-best', () => ({}))

  function load() {
    if (typeof localStorage === 'undefined') return
    try {
      best.value = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? '{}')
    } catch {
      best.value = {}
    }
  }

  function save() {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(best.value))
  }

  /** ☁️ Cloud merge (v3.22, per Javi's coworker: puzzle progress did
   * NOT sync). Progress only grows: union of completed levels, best
   * (lowest) damage wins per level. */
  function adopt(remote: Record<string, number>) {
    const merged = { ...best.value }
    let changed = false
    for (const [level, damage] of Object.entries(remote ?? {})) {
      const lv = Number(level)
      const dmg = Number(damage)
      if (!Number.isFinite(lv) || !Number.isFinite(dmg)) continue
      const next = merged[lv] === undefined ? dmg : Math.min(merged[lv]!, dmg)
      if (next !== merged[lv]) {
        merged[lv] = next
        changed = true
      }
    }
    // ☁️ v7.17 (follow-up review): only a real improvement replaces the
    // ref - see useAscension.load for why an equal replacement is not free.
    if (!changed) return
    best.value = merged
    save()
  }

  const highestCompleted = computed(() => {
    let lv = 0
    while (lv < PUZZLE_LEVEL_COUNT && best.value[lv + 1] !== undefined) lv++
    return lv
  })

  function unlocked(level: number): boolean {
    return level >= 1 && level <= PUZZLE_LEVEL_COUNT && level <= highestCompleted.value + 1
  }

  const total = computed(() => puzzleTotalScore(best.value))

  /** Builds the disposable session run and enters the encounter. */
  function start(level: number) {
    if (!unlocked(level)) return
    // Park the real run (only if this isn't already a puzzle session).
    if (typeof localStorage !== 'undefined' && typeof run.value?.puzzleLevel !== 'number') {
      const raw = localStorage.getItem(RUN_STORAGE_KEY)
      if (raw) localStorage.setItem(STASH_KEY, raw)
      else localStorage.removeItem(STASH_KEY)
    }

    const def = buildPuzzleLevel(level)
    const roster: Creature[] = def.party.map(m => ({
      id: makeId(m.defId),
      defId: m.defId,
      name: m.name,
      level: 1,
      xp: 0,
      stats: { ...m.stats },
      speed: m.speed,
      fight: m.fight,
      injured: false,
      available: true,
      pendingStatPoints: 0,
      assignedMissionId: null,
      surgePrimed: false,
      surgeCooldown: 0,
    }))

    // 🧩 v6.28: the puzzle HALL (per Javi, every puzzle plays there) in
    // the biome that cycles with the level, in the shape that fits the
    // screen — and trials on its pedestals, the same placement rule as a
    // real puzzle node (useEncounter.ensureEncounter).
    const theme = FOREST_THEMES[(level - 1) % FOREST_THEMES.length]!
    const forest = forestById(puzzleHallId(theme, isLandscapeViewport()))!
    def.missions.forEach((mission, index) => {
      const pi = index % forest.pois.length
      const poi = forest.pois[pi]!
      mission.x = poi.x
      mission.y = (poi.y / 100) * FIELD_HEIGHT
      mission.poi = pi
      const walkPx = poiTravelLength(poi) * (FIELD_HEIGHT / 100)
      mission.distance = Math.min(
        TUNING.travel.maxDistance,
        Math.max(TUNING.travel.minDistance, Math.round(walkPx / DISTANCE_SCALE)),
      )
      mission.puzzleAnswer = []
    })

    const node = {
      id: 'pz-node',
      row: 0,
      col: 1,
      type: 'puzzle' as const,
      next: [],
      preview: {
        requiredSuccesses: def.missions.length,
        focusElements: [],
        tier: 'easy' as const,
      },
    }

    const session = normalizeRun({
      phase: 'encounter',
      puzzleLevel: level,
      // The session can't be lost to damage — the score IS the damage.
      hp: 999,
      maxHp: 999,
      roster,
      map: { rows: 1, rowsPerAct: 1, nodes: [node] },
      activeNodeId: node.id,
      bossIntroSeen: true,
    })
    session.encounter = {
      nodeId: node.id,
      goal: def.missions.length,
      successes: 0,
      forestId: forest.id,
      missions: def.missions,
      decorations: [],
      returnTrips: [],
      nextSpawnIn: null,
      status: 'active',
      safetyNetUsed: false,
      medicBandUsed: false,
      perfectPlanUsed: false,
      fastDeployUsed: false,
      teamJournalPrimed: false,
      focusBellUses: 0,
      nextGoldSpawnIn: null,
      spawnCount: 0,
      everAssignedIds: [],
      succeededIds: [],
      damageTaken: 0,
    }
    run.value = session
    navigateTo('/encounter')
  }

  /** Books a finished level (keeps the best = lowest damage). */
  /** 🌟 What the LAST recorded result paid, so the summary can show it.
   * Null on a replay — the payout is once per level, ever. */
  const lastPuzzleReward = useState<number | null>('puzzle-last-reward', () => null)

  function recordResult(level: number, damage: number) {
    const prior = best.value[level]
    // 🌟 v3.75 (per Javi): a level pays stardust the FIRST time it is
    // cleared. Gated on `prior` rather than on the score, so a scrappy
    // clear still pays and a replay never does — including a replay that
    // beats the old damage, which still improves the best below.
    if (prior === undefined) {
      const reward = puzzleClearStardust(level)
      lastPuzzleReward.value = reward
      // 🏅 v4.15 gated this through /api/bonus/claim; 🏦 v4.92 the wallet
      // earn route checks-and-marks the SAME bonusClaims doc atomically
      // with the credit — the reset button still can't re-pay.
      useStardust().earnEvent({ kind: 'puzzle-first', level })
    } else {
      lastPuzzleReward.value = null
    }
    best.value = { ...best.value, [level]: prior === undefined ? damage : Math.min(prior, damage) }
    save()
    // 🏆 Separate puzzle leaderboard — fire and forget.
    const t = puzzleTotalScore(best.value)
    submitPuzzleProgress({ highestLevel: t.highest, damageTotal: t.damage })
  }

  /** Wipes local progress (v1.31): bests and unlocks start over. The
   * leaderboard entry stays — it's the all-time best by design. */
  function reset() {
    best.value = {}
    save()
  }

  /** Ends the session and restores the parked real run (if any). */
  function endSession() {
    if (typeof localStorage === 'undefined') return
    const raw = localStorage.getItem(STASH_KEY)
    localStorage.removeItem(STASH_KEY)
    try {
      run.value = raw ? normalizeRun(JSON.parse(raw) as Partial<RunState>) : null
    } catch {
      run.value = null
    }
  }

  return { best, load, adopt, highestCompleted, unlocked, total, start, recordResult, endSession, reset, lastPuzzleReward }
}
